import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Orders section — the order-management workspace for the active tenant.
 *
 * The order list (search + table) will render here once the OMS data API is
 * wired; today it surfaces the configuration entry point. Order lifecycle and
 * OMS behaviour are configured behind the header gear.
 */
export function OrdersPage() {
  const { t } = useTranslation('app');
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.orders.view');
  const tenantLabel = useMemo(() => tenantName ?? t('orders.fallbackTenant'), [tenantName, t]);

  return (
    <SectionScaffold
      title={t('orders.title')}
      subtitle={t('orders.subtitle')}
      canView={canView}
      settingsTo="/settings/orders"
      settingsLabel={t('orders.settingsLabel')}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('orders.searchPlaceholder'),
        disabled: true,
        disabledHint: t('orders.searchDisabledHint'),
      }}
    >
      <EmptyState
        icon={<ReceiptLongRoundedIcon sx={{ fontSize: 48 }} />}
        title={t('orders.empty.title')}
        description={t('orders.empty.description', { tenant: tenantLabel })}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/orders"
            startIcon={<SettingsRoundedIcon />}
          >
            {t('orders.empty.action')}
          </Button>
        }
      />
    </SectionScaffold>
  );
}
